import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { Radio } from "@eventbrite/marmalade"

// types
import { Categories, CategoryTitleCounts } from "../../SearchResultsTemplate"
import { SingleHit } from "../../../../services/help_articles.service"

// styles
import * as styles from "./SearchResultsSideNav.module.scss"
import classNames from "classnames"
import { MicroCopy } from "../../../ContactForm/ContactForm"
import { renderRichText } from "../../../../utils/contentful/render_rich_text"

export const ALL_RESULTS_VALUE = "allResults"

export type SideNavProps = {
  searchResultData: {
    found: number
    hit: SingleHit[]
  }
  queryData: Queries.SearchResultsTemplateDataQuery
  categoryTitleCounts: CategoryTitleCounts
  setActiveFilter: React.Dispatch<React.SetStateAction<string>>
  setPaginationIndex: React.Dispatch<React.SetStateAction<number>>
  filterSearchAllResultsCopy: MicroCopy
  rightOrientedRadios?: boolean
}

export const SearchResultsSideNav: FC<SideNavProps> = ({
  searchResultData,
  queryData,
  categoryTitleCounts,
  setActiveFilter,
  setPaginationIndex,
  filterSearchAllResultsCopy,
  rightOrientedRadios = false
}) => {
  const [selected, setSelected] = useState(ALL_RESULTS_VALUE)

  const handleSelected = useCallback(
    (value: string) => {
      setSelected(value)
    },
    [selected]
  )

  const isChecked = useCallback(
    (value: string) => value === selected,
    [selected]
  )

  const categories = useMemo<Categories & { [key: string]: any[] }>(() => {
    const catObj: Categories & { [key: string]: any[] } = {
      attendee: [],
      organizer: []
    }

    if (queryData?.allContentfulCategoryMenu.totalCount) {
      queryData?.allContentfulCategoryMenu?.nodes?.forEach((categoryMenu) => {
        const lowerCaseCategoryType = categoryMenu?.categoryAudience?.english?.toLowerCase()
        
        if (lowerCaseCategoryType) {
          if (!catObj[lowerCaseCategoryType]) {
            catObj[lowerCaseCategoryType] = []
          }
          catObj[lowerCaseCategoryType].push(categoryMenu)
        }
      })
    }

    return catObj
  }, [queryData, categoryTitleCounts, selected])

  const attendeeFilterComponentList = useMemo(() => {
    if (!categories.attendee || categories.attendee.length === 0) {
      return null
    }
    
    return (
      <FilterRadioList
        data={categories.attendee}
        isChecked={isChecked}
        handleSelected={handleSelected}
        headerString="Attendee topics"
        categoryTitleCounts={categoryTitleCounts}
        selected={selected}
        rightOrientedRadios={rightOrientedRadios}
        setPaginationIndex={setPaginationIndex}
      />
    )
  }, [categories, categoryTitleCounts, selected])

  const organizerFilterComponentList = useMemo(() => {
    if (!categories.organizer || categories.organizer.length === 0) {
      return null
    }
    
    return (
      <FilterRadioList
        data={categories.organizer}
        isChecked={isChecked}
        handleSelected={handleSelected}
        headerString="Organizer topics"
        categoryTitleCounts={categoryTitleCounts}
        selected={selected}
        rightOrientedRadios={rightOrientedRadios}
        setPaginationIndex={setPaginationIndex}
      />
    )
  }, [categories, categoryTitleCounts, selected])

  // rendering the additional categories which seem to be hidden
  const otherCategoriesLists = useMemo(() => {
    const otherCategoryKeys = Object.keys(categories).filter(
      key => !['attendee', 'organizer'].includes(key) && categories[key].length > 0
    )

    return otherCategoryKeys.map(categoryKey => (
      <FilterRadioList
        key={categoryKey}
        data={categories[categoryKey]}
        isChecked={isChecked}
        handleSelected={handleSelected}
        headerString={`${categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1)} topics`}
        categoryTitleCounts={categoryTitleCounts}
        selected={selected}
        rightOrientedRadios={rightOrientedRadios}
        setPaginationIndex={setPaginationIndex}
      />
    ))
  }, [categories, categoryTitleCounts, selected])

  useEffect(() => {
    setActiveFilter(selected)
  }, [selected])

  const RadioElement = useMemo(() => {
    return (
      <Radio checked={isChecked(ALL_RESULTS_VALUE)} value={ALL_RESULTS_VALUE} />
    )
  }, [rightOrientedRadios, searchResultData, selected])
  
  const SpanElement = useMemo(() => {
    return (
      <span
        className={classNames({
          [styles?.leftSpan]: !rightOrientedRadios,
          [styles?.rightSpan]: rightOrientedRadios,
          [styles?.mobileLabel]: rightOrientedRadios
        })}
      >
        {renderRichText(filterSearchAllResultsCopy?.raw)}
        {searchResultData?.found ? ` (${searchResultData?.found})` : ""}
      </span>
    )
  }, [rightOrientedRadios, searchResultData, selected])

  if (!searchResultData || !searchResultData?.found) {
    return null
  }

  return (
    <fieldset>
      <div className={styles?.singleCheckboxContainer}>
        <label
          htmlFor={ALL_RESULTS_VALUE}
          onClick={() => {
            setPaginationIndex(0)
            handleSelected(ALL_RESULTS_VALUE)
          }}
        >
          {rightOrientedRadios ? SpanElement : RadioElement}
          {rightOrientedRadios ? RadioElement : SpanElement}
        </label>
      </div>

      {attendeeFilterComponentList}
      {organizerFilterComponentList}
      {otherCategoriesLists}
    </fieldset>
  )
}

export const FilterRadioList: FC<{
  data: Queries.SearchResultsTemplateDataQuery["allContentfulCategoryMenu"]["nodes"]
  isChecked: (value: string, currentlySelected: string) => boolean
  categoryTitleCounts: CategoryTitleCounts
  selected: string
  handleSelected: (value: string) => void
  rightOrientedRadios: boolean
  setPaginationIndex: React.Dispatch<React.SetStateAction<number>>
  headerString?: string
}> = ({
  data,
  headerString,
  isChecked,
  categoryTitleCounts,
  selected,
  handleSelected,
  rightOrientedRadios,
  setPaginationIndex
}) => {
  const elements = useMemo(() => {
    return data.map((x, index) => {
      const RadioElement = () => (
        <Radio
          checked={isChecked(x.contentful_id, selected)}
          value={x.contentful_id}
        />
      )

      const count = categoryTitleCounts[x?.contentful_id] || 0

      const SpanElement = () => (
        <span
          className={classNames({
            [styles?.leftSpan]: !rightOrientedRadios,
            [styles?.rightSpan]: rightOrientedRadios,
            [styles?.mobileLabel]: rightOrientedRadios
          })}
        >
          {x.categoryTitle} ({count})
        </span>
      )

      return (
        <div
          key={x?.contentful_id + index}
          className={styles?.singleCheckboxContainer}
        >
          {index === 0 && headerString ? (
            <h6 className={styles?.topicHeading}>
              {headerString}
            </h6>
          ) : null}

          <label
            htmlFor={x.contentful_id}
            onClick={() => {
              setPaginationIndex(0)
              handleSelected(x.contentful_id)
            }}
          >
            <>
              {rightOrientedRadios ? <SpanElement /> : <RadioElement />}
              {rightOrientedRadios ? <RadioElement /> : <SpanElement />}
            </>
          </label>
        </div>
      )
    })
  }, [data, headerString, selected, categoryTitleCounts, isChecked, handleSelected, rightOrientedRadios, setPaginationIndex])

  return <>{elements}</>
}